import React, { useState, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { ErrorMessage } from '@hookform/error-message';
import { toast } from 'react-toastify';
import PauseService from '../service';
import QueueService from '../../Queue/service';
import SimpleHeader from 'components/Headers/SimpleHeader.js';
import { useHistory } from 'react-router-dom';
import FlexSelect from 'components/Inputs/FlexSelect';
import { ScreenPrompt } from 'components/ScreenPrompt';
import { useSelector } from 'react-redux';

import {
  Container,
  Row,
  Card,
  Form,
  Col,
  Input,
  CardHeader,
  CardBody
} from 'reactstrap';
import ReactLoader from 'components/Loader';
import ReactButton from 'components/Buttons/ReactButton';
import { buttonStyle } from 'components/Containers/ConfigurationContainer/components/utils';
import { MdClear } from 'react-icons/md';

const PauseForm = (props) => {
  const systemConfiguration = useSelector((state) => state.systemConfiguration);
  const history = useHistory();
  const { id } = props.match.params;

  const {
    reset,
    control,
    handleSubmit,
    errors,
    setValue,
    formState,
    watch,
    setError,
    clearErrors
  } = useForm();
  const isSoundActive = watch('is_sound_active');
  const isRepeatPeriodicActive = watch('is_repeat_period_active');
  const { isDirty } = formState;

  const [isUpdate, setIsUpdate] = useState(false);
  const [queues, setQueues] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sound, setSound] = useState(null);
  const [base64Sound, setBase64Sound] = useState(null);
  const [isValidSound, setIsValidSound] = useState(false);

  useEffect(() => {
    setLoading(true);
    QueueService.getAll()
      .then((response) => {
        setQueues(response.data.data);
        setLoading(false);
      })
      .catch((err) => {
        console.log('err: ' + err);
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    if (sound) {
      fileToBase64(sound).then((base64) => setBase64Sound(base64));
    }
  }, [sound]);

  useEffect(() => {
    if (id) {
      setLoading(true);
      PauseService.get(id)
        .then((response) => {
          let getPause = response.data.data;

          let arrayQueuesId = [];
          getPause.queues.forEach((queue) => {
            arrayQueuesId.push(queue.id);
          });
          getPause = { ...getPause, queues: arrayQueuesId };

          reset(getPause);
          setIsUpdate(true);
          setBase64Sound(getPause.sound);

          setLoading(false);
        })
        .catch((err) => {
          toast.error(err.response.data.message || 'Erro ao carregar pausa!', {
            autoClose: 3000,
            closeOnClick: true
          });
          setLoading(false);
        });
    }
  }, [id, reset]);

  const onSubmit = async (data) => {
    try {
      let formatedData = {
        ...data
      };

      if (sound) {
        formatedData.type_sound = data.sound.type;
        formatedData.name_sound = data.sound.name;
        formatedData.sound = await fileToBase64(sound);
      }
      const keysToRemove = new Set(
        [
          !isRepeatPeriodicActive ? 'period' : null,
          (sound === null && base64Sound !== null) || isSoundActive === false
            ? 'sound'
            : null
        ].filter(Boolean)
      );

      formatedData = Object.fromEntries(
        Object.entries(formatedData).filter(([key]) => !keysToRemove.has(key))
      );

      isUpdate
        ? await PauseService.update(id, formatedData)
        : await PauseService.create(formatedData);
      history.push('/admin/pause');

      toast.success(
        isUpdate
          ? 'Pausa alterada com sucesso!'
          : 'Pausa cadastrada com sucesso!',
        {
          autoClose: 3000,
          closeOnClick: true
        }
      );
    } catch (err) {
      toast.error(err.response?.data?.message || 'Ops, ocorreu um erro!', {
        autoClose: 3000,
        closeOnClick: true
      });
    }
  };

  const errorFormMessage = (message) => (
    <p style={{ color: 'red' }}>{message}</p>
  );

  const cancelAction = () => {
    history.push('/admin/pause');
  };

  const changeFile = (event) => {
    const fileInput = event.target;
    if (fileInput.files.length === 0) return;

    const file = fileInput.files[0];

    fileInput.value = '';

    if (isFileValid(file)) {
      reset({ ...watch(), sound: file });
      setSound(file);
    }
  };

  const fileToBase64 = (file) => {
    return new Promise((resolve) => {
      var reader = new FileReader();
      reader.onload = function (event) {
        resolve(event.target.result);
      };

      reader.readAsDataURL(file);
    });
  };

  const isFileValid = (file) => {
    if (!file) {
      setError('sound', {
        type: 'required',
        message: 'Selecione um arquivo de áudio'
      });
      return false;
    }
    if (file.size > 5 * 1024 * 1024) {
      setError('sound', {
        type: 'maxLength',
        message: 'Arquivo maior do que 5M'
      });
      return false;
    }
    clearErrors('sound');
    return true;
  };

  const handleClearAudio = () => {
    setValue('sound', null);
    setSound(null);
    setBase64Sound(null);
    setIsValidSound(false);
    clearErrors('sound');
  };

  return (
    <>
      <SimpleHeader
        name="Form validation"
        parentName="Forms"
        returnPath="/admin/pause"
        buttonTitle="Voltar para a pagina de pausas"
      />
      <ScreenPrompt
        when={isDirty && !formState.isSubmitting}
        message={`Está certo de que deseja sair? Terá que preencher os dados novamente quando retornar`}
      />
      <Container className="mt--6" fluid>
        <Row>
          <div className="col">
            <div className="card-wrapper">
              <Card>
                <CardHeader>
                  <h3>Pausa</h3>
                </CardHeader>
                <CardBody>
                  {loading ? (
                    <Row className="justify-content-md-center">
                      <ReactLoader />
                    </Row>
                  ) : (
                    <Form
                      className="needs-validation"
                      onSubmit={handleSubmit(onSubmit)}
                    >
                      <Row className="mb-3">
                        <Col>
                          <h3 className="mb-1">Principal</h3>
                        </Col>
                      </Row>
                      <Row className="mb-3">
                        <Col md="6">
                          <label className="form-control-label">Nome*</label>
                          <Controller
                            as={Input}
                            control={control}
                            name="name"
                            rules={{ required: 'Nome é obrigatório!' }}
                            defaultValue=""
                          />
                          <ErrorMessage
                            errors={errors}
                            name="name"
                            render={({ message }) => errorFormMessage(message)}
                          />
                        </Col>

                        <Col md="6">
                          <label className="form-control-label">
                            Descrição
                          </label>
                          <Controller
                            as={Input}
                            control={control}
                            name="description"
                            defaultValue=""
                          />
                        </Col>
                      </Row>
                      <Row className="mb-3">
                        <Col md="6">
                          <label className="form-control-label">
                            Tempo de Alerta* (Segundos)
                          </label>
                          <Controller
                            as={Input}
                            control={control}
                            name="time_alert"
                            rules={{
                              required: 'Tempo de alerta é obrigatório!',
                              pattern: {
                                value: /^\d+$/,
                                message: 'Tempo de alerta é um campo numérico!'
                              }
                            }}
                            defaultValue=""
                          />
                          <ErrorMessage
                            errors={errors}
                            name="time_alert"
                            render={({ message }) => errorFormMessage(message)}
                          />
                        </Col>
                      </Row>
                      <Row className="mb-3">
                        <Col className="md-6 mx-4">
                          <Controller
                            name="active"
                            control={control}
                            render={(props) => (
                              <>
                                <label className="form-control-label">
                                  <Input
                                    type="checkbox"
                                    onChange={(e) => {
                                      props.onChange(e.target.checked);
                                    }}
                                    checked={props.value}
                                  />
                                  Ativa
                                </label>
                              </>
                            )}
                            defaultValue=""
                          />
                        </Col>
                      </Row>
                      <Row className="mb-3">
                        <Col className="md-6 mx-4">
                          <Controller
                            name="is_notification_active"
                            control={control}
                            render={(props) => (
                              <>
                                <label className="form-control-label">
                                  <Input
                                    type="checkbox"
                                    onChange={(e) => {
                                      props.onChange(e.target.checked);
                                    }}
                                    checked={props.value}
                                  />
                                  Alerta Notificação
                                </label>
                              </>
                            )}
                            defaultValue=""
                          />
                        </Col>
                      </Row>
                      <Row className="mb-3">
                        <Col className="md-6 mx-4">
                          <Controller
                            name="is_sound_active"
                            control={control}
                            render={(props) => (
                              <>
                                <label className="form-control-label">
                                  <Input
                                    type="checkbox"
                                    onChange={(e) => {
                                      props.onChange(e.target.checked);
                                    }}
                                    checked={props.value}
                                  />
                                  Alerta Sonoro
                                </label>
                              </>
                            )}
                            defaultValue=""
                          />
                        </Col>
                        <Col className="md-6 mx-4">
                          <Controller
                            name="is_repeat_period_active"
                            control={control}
                            render={(props) => (
                              <>
                                <label className="form-control-label">
                                  <Input
                                    type="checkbox"
                                    onChange={(e) => {
                                      props.onChange(e.target.checked);
                                    }}
                                    checked={props.value}
                                  />
                                  Repetir Periodicamente
                                </label>
                              </>
                            )}
                            defaultValue=""
                          />
                        </Col>
                      </Row>
                      <Row className="mb-3">
                        <Col sm="6" className="mt-3">
                          <label className="form-control-label d-block">
                            Áudio (mp3, mp4, wav, gsm, ogg, limite de 5 Mb)*
                          </label>
                          <label
                            htmlFor="sound"
                            className={`btn btn-primary btn-sm ${
                              !isSoundActive ? 'disabled' : ''
                            }`}
                            style={buttonStyle(
                              systemConfiguration.primary_color
                            )}
                          >
                            Selecionar um arquivo
                          </label>
                          <Controller
                            name="sound"
                            control={control}
                            rules={{
                              required: isSoundActive
                                ? 'O arquivo de áudio é obrigatório!'
                                : false
                            }}
                            render={() => (
                              <Input
                                style={{ display: 'none' }}
                                type="file"
                                id="sound"
                                accept="audio/mp3, audio/mp4, audio/wav, audio/gsm, audio/ogg"
                                disabled={!isSoundActive}
                                onChange={changeFile}
                              />
                            )}
                            defaultValue=""
                          />
                          <ErrorMessage
                            errors={errors}
                            name="sound"
                            render={({ message }) => errorFormMessage(message)}
                          />

                          <Col sm="6" className="me-auto">
                            {(base64Sound !== null && isSoundActive === true) ||
                            (isFileValid && sound) ? (
                              <div
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '1rem'
                                }}
                              >
                                <audio
                                  src={
                                    base64Sound !== null
                                      ? base64Sound
                                      : window.URL.createObjectURL(sound)
                                  }
                                  controls
                                  style={{
                                    width: '100%',
                                    maxWidth: '20rem',
                                    marginLeft: '-20px'
                                  }}
                                ></audio>
                                <button
                                  type="button"
                                  className="btn btn-danger"
                                  onClick={handleClearAudio}
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center'
                                  }}
                                >
                                  <MdClear title="Remover arquivo selecionado" />
                                </button>
                              </div>
                            ) : null}
                          </Col>
                        </Col>

                        <Col md="6">
                          <label className="form-control-label">
                            Período <small>(Segundos)</small>
                          </label>
                          <Controller
                            as={Input}
                            control={control}
                            name="period"
                            disabled={!isRepeatPeriodicActive}
                            rules={{
                              required: isRepeatPeriodicActive
                                ? 'O período é obrigatório!'
                                : false,
                              pattern: {
                                value: /^\d+$/,
                                message: 'O período deve ser um número!'
                              },
                              validate: (value) => {
                                if (!isRepeatPeriodicActive) return true;
                                return (
                                  parseInt(value, 10) > 0 ||
                                  'O período deve ser maior que 0!'
                                );
                              }
                            }}
                            defaultValue=""
                          />
                          <ErrorMessage
                            errors={errors}
                            name="period"
                            render={({ message }) => errorFormMessage(message)}
                          />
                        </Col>
                      </Row>

                      <Row className="mb-3">
                        <Col>
                          <h3 className="mb-1">Configurações</h3>
                        </Col>
                      </Row>

                      <Row className="mb-3">
                        <Col md="6">
                          <label className="form-control-label">Fila(s)</label>
                          <Controller
                            render={(props) => (
                              <FlexSelect
                                dataOptions={queues}
                                isMulti={true}
                                isClearable={true}
                                value={props.value}
                                valueController={setValue}
                                fieldName="queues"
                                labelName="name"
                                valueName="id"
                              />
                            )}
                            control={control}
                            name="queues"
                            defaultValue=""
                            rules={{
                              required: 'Selecione as as filas da pausa!'
                            }}
                          />
                          <ErrorMessage
                            errors={errors}
                            name="queues"
                            render={({ message }) => errorFormMessage(message)}
                          />
                        </Col>
                      </Row>

                      <hr />
                      <ReactButton btnColor="confirmation" type="submit">
                        Salvar
                      </ReactButton>
                      <ReactButton
                        btnColor="cancelation"
                        onClick={() => cancelAction()}
                      >
                        Cancelar
                      </ReactButton>
                    </Form>
                  )}
                </CardBody>
              </Card>
            </div>
          </div>
        </Row>
      </Container>
    </>
  );
};

export default PauseForm;
